import { useState } from "react";
import funnelApi from "../services/funnelApi";
import { notification } from "../services/notification";

interface FunnelModalProps {
    onClose: () => void;
    onSaved: () => void;
}

function FunnelModal({ onClose, onSaved }: FunnelModalProps) {
    const [title, setTitle] = useState("");
    const [slug, setSlug] = useState("");
    const [ctaLink, setCtaLink] = useState("");
    const [prompt, setPrompt] = useState("");
    const [generatedHtml, setGeneratedHtml] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [activeTab, setActiveTab] = useState<"ai" | "paste">("ai");
    const [showPreview, setShowPreview] = useState(false);

    const handleTitleChange = (value: string) => {
        setTitle(value);
        if (!slug) {
            const newSlug = value
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "");
            setSlug(newSlug);
        }
    };

    const handleGenerate = async () => {
        if (!title.trim()) {
            notification.error("Please enter a title");
            return;
        }
        if (!ctaLink.trim()) {
            notification.error("Please enter a Cart URL");
            return;
        }
        if (!prompt.trim()) {
            notification.error("Please enter your prompt");
            return;
        }

        setIsGenerating(true);
        try {
            const result = await funnelApi.generateWithAI({
                title,
                slug,
                ctaLink,
                prompt,
            });

            if (result.success && result.html) {
                setGeneratedHtml(result.html);
                notification.success("Funnel generated successfully!");
                setShowPreview(true);
            } else {
                notification.error(result.error || "Failed to generate funnel");
            }
        } catch (error) {
            notification.error("Failed to generate funnel");
        }
        setIsGenerating(false);
    };

    const handlePasteHtml = () => {
        if (!title.trim()) {
            notification.error("Please enter a title");
            return;
        }
        if (!generatedHtml.trim()) {
            notification.error("Please paste HTML content");
            return;
        }
        setShowPreview(true);
    };

    const handleSave = async () => {
        if (!title.trim()) {
            notification.error("Please enter a title");
            return;
        }
        if (!generatedHtml.trim()) {
            notification.error("Please generate or paste HTML first");
            return;
        }

        setIsSaving(true);
        try {
            const result = await funnelApi.saveFunnel({
                title,
                slug: slug || title.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
                generatedHtml,
            });

            if (result.success) {
                notification.success("Funnel saved successfully!");
                onSaved();
            } else {
                notification.error(result.error || "Failed to save funnel");
            }
        } catch (error) {
            notification.error("Failed to save funnel");
        }
        setIsSaving(false);
    };

    const handleDownload = () => {
        if (!generatedHtml) return;

        const blob = new Blob([generatedHtml], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${slug || title || "funnel"}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
        }}>
            <div style={{
                background: "white",
                borderRadius: "16px",
                width: "100%",
                maxWidth: "900px",
                maxHeight: "90vh",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
            }}>
                <div style={{
                    padding: "20px 24px",
                    borderBottom: "1px solid #e5e7eb",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}>
                    <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: "700" }}>
                        Generate Sales Funnel
                    </h2>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            background: "#f3f4f6",
                            border: "none",
                            width: "36px",
                            height: "36px",
                            borderRadius: "8px",
                            cursor: "pointer",
                        }}
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div style={{ flex: 1, overflow: "auto", padding: "24px" }}>
                    <div style={{ display: "flex", gap: "12px", marginBottom: "24px" }}>
                        <button
                            onClick={() => setActiveTab("ai")}
                            style={{
                                padding: "10px 20px",
                                borderRadius: "8px",
                                border: "none",
                                background: activeTab === "ai" ? "linear-gradient(135deg, #6366f1, #8b5cf6)" : "#f3f4f6",
                                color: activeTab === "ai" ? "white" : "#6b7280",
                                fontWeight: "600",
                                cursor: "pointer",
                            }}
                        >
                            <i className="fas fa-robot"></i> Generate with AI
                        </button>
                        <button
                            onClick={() => setActiveTab("paste")}
                            style={{
                                padding: "10px 20px",
                                borderRadius: "8px",
                                border: "none",
                                background: activeTab === "paste" ? "linear-gradient(135deg, #6366f1, #8b5cf6)" : "#f3f4f6",
                                color: activeTab === "paste" ? "white" : "#6b7280",
                                fontWeight: "600",
                                cursor: "pointer",
                            }}
                        >
                            <i className="fas fa-code"></i> Paste HTML
                        </button>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                        <label style={{ display: "block", fontSize: "0.9rem", fontWeight: "600", color: "#374151", marginBottom: "6px" }}>
                            Title *
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => handleTitleChange(e.target.value)}
                            placeholder="e.g., Wireless Headset Pro"
                            style={{
                                width: "100%",
                                padding: "12px",
                                borderRadius: "8px",
                                border: "1px solid #d1d5db",
                                fontSize: "0.95rem",
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                        <label style={{ display: "block", fontSize: "0.9rem", fontWeight: "600", color: "#374151", marginBottom: "6px" }}>
                            URL Slug
                        </label>
                        <input
                            type="text"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            placeholder="e.g., wireless-headset-pro"
                            style={{
                                width: "100%",
                                padding: "12px",
                                borderRadius: "8px",
                                border: "1px solid #d1d5db",
                                fontSize: "0.95rem",
                            }}
                        />
                        <p style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "4px" }}>
                            Will be accessible at: /funnel/{slug || "your-slug"}
                        </p>
                    </div>

                    {activeTab === "ai" ? (
                        <>
                            <div style={{ marginBottom: "16px" }}>
                                <label style={{ display: "block", fontSize: "0.9rem", fontWeight: "600", color: "#374151", marginBottom: "6px" }}>
                                    Buy Now CTA Link (Cart URL) *
                                </label>
                                <input
                                    type="text"
                                    value={ctaLink}
                                    onChange={(e) => setCtaLink(e.target.value)}
                                    placeholder="e.g., http://localhost:3000/cart?productId=PROD-XXX"
                                    style={{
                                        width: "100%",
                                        padding: "12px",
                                        borderRadius: "8px",
                                        border: "1px solid #d1d5db",
                                        fontSize: "0.95rem",
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: "16px" }}>
                                <label style={{ display: "block", fontSize: "0.9rem", fontWeight: "600", color: "#374151", marginBottom: "6px" }}>
                                    Enter your prompt *
                                </label>
                                <textarea
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    placeholder="Write your prompt for the AI to generate the sales funnel... Example: Create a high-converting sales funnel for TWS Earphones. Use Tailwind CSS. Include hero section, features, testimonials, and a buy now CTA button linking to my cart."
                                    rows={6}
                                    style={{
                                        width: "100%",
                                        padding: "12px",
                                        borderRadius: "8px",
                                        border: "1px solid #d1d5db",
                                        fontSize: "0.95rem",
                                        resize: "vertical",
                                        fontFamily: "inherit",
                                    }}
                                />
                            </div>

                            <button
                                onClick={handleGenerate}
                                disabled={isGenerating}
                                style={{
                                    width: "100%",
                                    padding: "14px",
                                    borderRadius: "10px",
                                    border: "none",
                                    background: isGenerating ? "#9ca3af" : "linear-gradient(135deg, #6366f1, #8b5cf6)",
                                    color: "white",
                                    fontSize: "1rem",
                                    fontWeight: "600",
                                    cursor: isGenerating ? "not-allowed" : "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "8px",
                                }}
                            >
                                {isGenerating ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        Generating with AI...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-robot"></i>
                                        Generate with AI
                                    </>
                                )}
                            </button>
                        </>
                    ) : (
                        <>
                            <div style={{ marginBottom: "16px" }}>
                                <label style={{ display: "block", fontSize: "0.9rem", fontWeight: "600", color: "#374151", marginBottom: "6px" }}>
                                    Paste HTML *
                                </label>
                                <textarea
                                    value={generatedHtml}
                                    onChange={(e) => setGeneratedHtml(e.target.value)}
                                    placeholder="Paste your HTML code here..."
                                    rows={15}
                                    style={{
                                        width: "100%",
                                        padding: "12px",
                                        borderRadius: "8px",
                                        border: "1px solid #d1d5db",
                                        fontSize: "0.85rem",
                                        resize: "vertical",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>

                            <button
                                onClick={handlePasteHtml}
                                disabled={!generatedHtml.trim()}
                                style={{
                                    width: "100%",
                                    padding: "14px",
                                    borderRadius: "10px",
                                    border: "none",
                                    background: generatedHtml.trim() ? "linear-gradient(135deg, #6366f1, #8b5cf6)" : "#9ca3af",
                                    color: "white",
                                    fontSize: "1rem",
                                    fontWeight: "600",
                                    cursor: generatedHtml.trim() ? "pointer" : "not-allowed",
                                }}
                            >
                                <i className="fas fa-eye"></i> Preview HTML
                            </button>
                        </>
                    )}

                    {showPreview && generatedHtml && (
                        <div style={{ marginTop: "24px" }}>
                            <div style={{ 
                                display: "flex", 
                                justifyContent: "space-between", 
                                alignItems: "center",
                                marginBottom: "12px" 
                            }}>
                                <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: "600" }}>Preview</h3>
                                <button
                                    onClick={handleDownload}
                                    style={{
                                        padding: "8px 16px",
                                        borderRadius: "8px",
                                        border: "1px solid #d1d5db",
                                        background: "white",
                                        color: "#374151",
                                        fontSize: "0.9rem",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                    }}
                                >
                                    <i className="fas fa-download"></i> Download HTML
                                </button>
                            </div>
                            <iframe
                                srcDoc={generatedHtml}
                                style={{
                                    width: "100%",
                                    height: "500px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "8px",
                                    background: "white",
                                }}
                                title="Funnel Preview"
                                sandbox="allow-scripts allow-same-origin"
                            />
                        </div>
                    )}
                </div>

                {generatedHtml && (
                    <div style={{
                        padding: "16px 24px",
                        borderTop: "1px solid #e5e7eb",
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "12px",
                    }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: "12px 24px",
                                borderRadius: "10px",
                                border: "1px solid #d1d5db",
                                background: "white",
                                color: "#374151",
                                fontSize: "0.95rem",
                                fontWeight: "600",
                                cursor: "pointer",
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            style={{
                                padding: "12px 24px",
                                borderRadius: "10px",
                                border: "none",
                                background: isSaving ? "#9ca3af" : "linear-gradient(135deg, #6366f1, #8b5cf6)",
                                color: "white",
                                fontSize: "0.95rem",
                                fontWeight: "600",
                                cursor: isSaving ? "not-allowed" : "pointer",
                            }}
                        >
                            {isSaving ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i> Saving...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-save"></i> Save Funnel
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default FunnelModal;
